import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { isMobile } from "react-device-detect";

import { Container, TableHead, Typography } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";
import { DropzoneArea } from "mui-file-dropzone";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";

import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import { DialogContent } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";

import SelectStudentsDialog from "../../components/faculty/SelectStudentsDialog";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";
import { readWorkbook } from "../../services/ExportService";
import { exportDownloadSingle } from "../../services/ExportService";

import Stack from "@mui/material/Stack";

import { doService } from "../../services/ServiceHelper";
import {
  addUserListMembers__import,
  getUserList,
  getUserListMembers_v2,
} from "../../services/UserService";
import {
  getSchoolStudents,
  addUserListMembers,
  removeUserListStudent,
  isAdmin,
} from "../../services/UserService";
import {
  StripedTableCell,
  StripedTableRow,
} from "../../components/StripedTable";
import isEmpty from "lodash-es/isEmpty";
import TableRowNoResults from "../../components/TableRowNoResults";

const StudentListMembers = () => {
  const { eid } = useParams();

  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);

  const [_dialogStudentsOpen, setDialogStudentsOpen] = useState(false);
  const [_dialogImportOpen, setDialogImportOpen] = useState(false);

  const [_studentList, setStudentList] = useState({});
  const [_listStudents, setListStudents] = useState([]);
  const [_allStudents, setAllStudents] = useState([]);

  useEffect(() => {
    initStudentList();
    initStudentListMembers();
    initStudents();
  }, []);

  const initStudents = () => {
    doService(getSchoolStudents(_school, _jwt), (results) => {
      const labeledStudents = [];
      for (const student of results.data) {
        let label = `${student.last_name}, ${student.first_name}`;

        if (!isEmpty(student.mi)) {
          label += ` ${student.mi}.`;
        }

        label += ` (${student.grade})`;

        if (!isEmpty(student.externalId)) {
          label += ` [${student.externalId}]`;
        }

        labeledStudents.push({
          label: label,
          value: student.eid,
        });
      }

      setAllStudents(labeledStudents);
    });
  };

  const initStudentList = () => {
    doService(getUserList(_school, _jwt, eid), (results) => {
      setStudentList(results.data);
    });
  };

  const initStudentListMembers = () => {
    setShowProgress(true);
    doService(getUserListMembers_v2(_school, _jwt, eid), (results) => {
      setListStudents(results.data);
      setShowProgress(false);
    });
  };

  const handleClickAdd = () => {
    setDialogStudentsOpen(true);
  };

  const handleStudentsClose = () => {
    setDialogStudentsOpen(false);
  };

  const handleStudentsOK = (selectedStudents) => {
    setDialogStudentsOpen(false);

    if (selectedStudents.length === 0) {
      return;
    }

    // collect IDs from raw values
    const studentEids = [];
    for (const student of selectedStudents) {
      studentEids.push(student.value);
    }

    setShowProgress(true);

    doService(
      addUserListMembers(_school, _jwt, eid, studentEids),
      (results) => {
        setShowProgress(false);
        window.showSnack("Students Added");
        initStudentListMembers();
      }
    );
  };

  const removeStudent = (student) => {
    doService(
      removeUserListStudent(_school, _jwt, eid, student.eid),
      (results) => {
        window.showSnack("Student Removed");
        initStudentListMembers();
      }
    );
  };

  const handleClickImport = () => {
    setDialogImportOpen(true);
  };

  const handleClickExport = () => {
    const rows = [];

    rows.push(["Last Name", "First Name", "Grade", "Email"]);

    for (let result of _listStudents) {
      rows.push([
        result.last_name,
        result.first_name,
        result.grade,
        result.email,
      ]);
    }
    exportDownloadSingle("student-list-members.xlsx", "Results", rows);
  };

  const handleChangeImportFiles = (files) => {
    if (files == null || files.length === 0) {
      return;
    }
    parseFile(files[0]);
  };

  const getValidRows = (rows) => {
    const okRows = [];

    for (let row of rows) {
      if (row == null || row.length === 0 || row.length < 4) {
        continue;
      }
      okRows.push({
        Email: row['Email'],
        StudentID: row['StudentID'],
        LastName: row['LastName'],
        FirstName: row['FirstName'],
      });
    }

    return okRows;
  };

  const parseFile = (fileObj) => {
    readWorkbook(fileObj).then((entries) => {
      const validRows = getValidRows(entries);
      console.log(validRows);

      if (validRows.length === 0) {
        alert("File is empty.  Please select another file.");
        return;
      }

      doImport(validRows);
    });
  };

  const doImport = (rows) => {
    doService(
      addUserListMembers__import(_school, _jwt, eid, rows),
      (results) => {
        setShowProgress(false);
        const count = results.data;
        window.showSnack(`${count} Student(s) Added`);
        initStudentListMembers();
      }
    );
    setDialogImportOpen(false);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Student List Members" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Alert severity="info" sx={{ mt: 1 }}>
          Click the buttons below to add students to the list, or click back to
          go to list of Student Lists.
        </Alert>

        <Alert severity="info" sx={{ mt: 1 }}>
          PLEASE download and use the template available{" "}
          <Link href="/data/user-list-template.xlsx">HERE</Link>. At a minimum,
          enter the student email address in the designated column.
        </Alert>

        <Accordion disableGutters sx={{ boxShadow: 1, mt: 2 }} expanded>
          <AccordionSummary className="accordionTitle">
            List Name: {_studentList.name}
          </AccordionSummary>
          <AccordionDetails>
            <InputLabel>Description:</InputLabel>
            <Typography>
              {isEmpty(_studentList.description)
                ? "-- NONE --"
                : _studentList.description}
            </Typography>
          </AccordionDetails>
        </Accordion>
        <br />

        {(_studentList.isGlobal === 0 || isAdmin(_jwt)) && (
          <Stack direction="row" spacing={1}>
            <Button variant="contained" fullWidth onClick={handleClickAdd}>
              Add Student(s)
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleClickImport}
              className="button1"
            >
              Import Students (XLSX)
            </Button>
            {isMobile === false && (
              <Button variant="outlined" fullWidth onClick={handleClickExport}>
                Export Students (XLSX)
              </Button>
            )}
          </Stack>
        )}

        <Accordion disableGutters sx={{ boxShadow: 1, mt: 2 }} expanded>
          <AccordionSummary className="accordionTitle">
            Student List ({_listStudents.length})
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={{ mt: 1, mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <StripedTableRow>
                    <StripedTableCell>Student</StripedTableCell>
                    <StripedTableCell align="center">Grade</StripedTableCell>
                    {(_studentList.isGlobal === 0 || isAdmin(_jwt)) && (
                      <StripedTableCell align="center" width={120}>
                        Action
                      </StripedTableCell>
                    )}
                  </StripedTableRow>
                </TableHead>
                <TableBody>
                  <TableRowNoResults data={_listStudents} />
                  {_listStudents.map((student) => (
                    <StripedTableRow key={student.eid}>
                      <StripedTableCell>
                        {student.last_name}, {student.first_name}
                        {isEmpty(student.mi) === false && <> {student.mi}.</>}
                        {!isEmpty(student.externalId) && (
                          <> ({student.externalId})</>
                        )}
                      </StripedTableCell>
                      <StripedTableCell align="center">
                        {student.grade}
                      </StripedTableCell>

                      {(_studentList.isGlobal === 0 || isAdmin(_jwt)) && (
                        <StripedTableCell align="center">
                          <Button
                            variant="outlined"
                            onClick={() => {
                              removeStudent(student);
                            }}
                          >
                            Remove
                          </Button>
                        </StripedTableCell>
                      )}
                    </StripedTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

      </Container>

      <SelectStudentsDialog
        open={_dialogStudentsOpen}
        onClose={handleStudentsClose}
        onOK={handleStudentsOK}
        students={_allStudents}
      />

      <Dialog fullWidth={true} open={_dialogImportOpen}>
        <DialogTitle sx={{ p: 1 }}>Select Input File</DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <Alert severity="info" sx={{ mt: 1 }}>
            PLEASE download and use the template available{" "}
            <Link href="/data/user-list-template.xlsx">HERE</Link>. At a
            minimum, enter the student email address in the designated column.
          </Alert>

          <DropzoneArea
            filesLimit={1}
            onChange={(files) => handleChangeImportFiles(files)}
            acceptedFiles={[
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ]}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setDialogImportOpen(false);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StudentListMembers;
